import * as React from 'react';
import type { CanonicalSource } from '@/lib/governance/derive-canonical-source';
import s from './canonical-sources.module.scss';

export interface AddCanonicalSourceFormProps {
  onCancel: () => void;
  onSubmit: (source: CanonicalSource) => Promise<void> | void;
  submitting?: boolean;
}

type Kind = 'npm' | 'directory';

export function AddCanonicalSourceForm({
  onCancel,
  onSubmit,
  submitting = false,
}: AddCanonicalSourceFormProps) {
  const [kind, setKind] = React.useState<Kind>('npm');
  const [value, setValue] = React.useState<string>('');
  const [excludeText, setExcludeText] = React.useState<string>('');
  const [error, setError] = React.useState<string | null>(null);

  function validate(): CanonicalSource | null {
    const trimmed = value.trim();
    if (!trimmed) {
      setError(
        kind === 'npm'
          ? 'Package name cannot be empty.'
          : 'Directory path cannot be empty.',
      );
      return null;
    }
    if (kind === 'directory') {
      if (trimmed.startsWith('/')) {
        setError('Path must be repo-relative (cannot start with "/").');
        return null;
      }
      if (trimmed.split('/').includes('..')) {
        setError('Path cannot contain "..".');
        return null;
      }
    }
    setError(null);

    const exclude = excludeText
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    if (kind === 'npm') {
      return {
        kind: 'npm',
        specifier: trimmed,
        ...(exclude.length > 0 ? { exclude } : {}),
      };
    }
    return {
      kind: 'directory',
      path: trimmed,
      ...(exclude.length > 0 ? { exclude } : {}),
    };
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const source = validate();
    if (!source) return;
    await onSubmit(source);
  }

  return (
    <form className={s.addForm} onSubmit={handleSubmit}>
      <div role="radiogroup" aria-label="Source kind" className={s.kindToggle}>
        <button
          type="button"
          role="radio"
          aria-checked={kind === 'npm'}
          className={s.kindToggleBtn}
          data-active={kind === 'npm'}
          onClick={() => setKind('npm')}
        >
          npm package
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={kind === 'directory'}
          className={s.kindToggleBtn}
          data-active={kind === 'directory'}
          onClick={() => setKind('directory')}
        >
          Directory
        </button>
      </div>

      <label className={s.inputLabel} htmlFor="canonical-source-value">
        {kind === 'npm'
          ? 'Package specifier'
          : 'Directory path (repo-relative)'}
      </label>
      <input
        id="canonical-source-value"
        className={s.input}
        type="text"
        autoComplete="off"
        spellCheck={false}
        placeholder={kind === 'npm' ? '@acme/ui' : './src/components/ui'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />

      <label className={s.inputLabel} htmlFor="canonical-source-exclude">
        Exclude (comma-separated, optional)
      </label>
      <input
        id="canonical-source-exclude"
        className={s.input}
        type="text"
        autoComplete="off"
        spellCheck={false}
        placeholder="Text, Icon"
        value={excludeText}
        onChange={(e) => setExcludeText(e.target.value)}
      />

      {error && (
        <p className={s.errorText} role="alert">
          {error}
        </p>
      )}

      <div className={s.addFormActions}>
        <button
          type="button"
          className={s.formBtn}
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={s.formBtn}
          data-variant="primary"
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Add source'}
        </button>
      </div>
    </form>
  );
}
